import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { parseAsInteger, useQueryState } from "nuqs";
import AuthContext from "../../context/AuthProvider";
import useCaseFilters from "../../features/cases/useCaseFilters";
import { sortByColumn } from "../../features/cases/caseFilters";
import CasesTable from "./CasesTable";
import CasesToolbar from "./CasesToolbar";
import TablePagination from "./TablePagination";
import BulkActionBar from "./BulkActionBar";
import useTablePrefs from "./useTablePrefs";
import useRowSelection from "./useRowSelection";
import useCaseMutations, { RECHECK_CAP } from "./useCaseMutations";
import useSplitPane from "./useSplitPane";
import CaseDetailPanel from "../detail/CaseDetailPanel";
import useHotkeys from "../hooks/useHotkeys";
import ShortcutsHelp from "../ShortcutsHelp";

const isFav = (c) => c?.fav === 1 || c?.fav === true;

// Fixed sort key for the ★ column.
const FAV_COLUMN = {
  id: "fav",
  sortValue: (c) => (isFav(c) ? 1 : 0),
};

const DEFAULT_PENDING_FILTER = () => true;

// Desktop list page shared by every case type: toolbar, sortable table,
// bulk actions and a resizable side panel (case detail, or an overview while
// nothing is open). Everything type-specific comes from `config` (see
// src/desktop/<type>/<type>Config.jsx); `list` is useCases() or useCaseList().
const CaseListDesktop = ({ config, list }) => {
  const navigate = useNavigate();
  const { credits, setCredits, checking } = useContext(AuthContext);
  const { allCases, loading, error, addCase, removeCase, updateCase, refresh } = list;
  const { filters, activeCount, search, page, setFilter, setSearch, clearFilters, setPage } =
    useCaseFilters(config.filterKeys);
  const [openId, setOpenId] = useQueryState("case", parseAsInteger);
  const { prefs, toggleColumn, resetColumns, cycleSort, setPageSize } = useTablePrefs(
    config.scope,
    config.columns
  );
  const [focusedId, setFocusedId] = useState(null);
  const [showAddCase, setShowAddCase] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [notice, setNotice] = useState(null);
  const searchRef = useRef(null);
  const noticeTimer = useRef(null);
  const { detail } = config;
  const fullPageHref = detail.fullPageHref ?? null;

  const notify = useCallback((text, tone = "ok") => {
    window.clearTimeout(noticeTimer.current);
    setNotice({ text, tone });
    noticeTimer.current = window.setTimeout(() => setNotice(null), 6000);
  }, []);
  useEffect(() => () => window.clearTimeout(noticeTimer.current), []);

  const mutations = useCaseMutations({
    allCases,
    updateCase,
    removeCase,
    notify,
    actions: config.actions,
    noun: config.noun,
  });
  const { caps } = mutations;

  // ---- derived list -------------------------------------------------------
  const searched = useMemo(
    () => (search ? allCases.filter((c) => config.search(c, search)) : allCases),
    [allCases, search, config]
  );
  const filtered = useMemo(
    () => (config.applyFilters ? config.applyFilters(searched, filters) : searched),
    [searched, filters, config]
  );
  const sorted = useMemo(() => {
    if (!prefs.sort) return filtered; // lists are already newest-first
    const column =
      prefs.sort.id === "fav" ? FAV_COLUMN : config.columnsById[prefs.sort.id];
    return sortByColumn(filtered, column, prefs.sort.dir);
  }, [filtered, prefs.sort, config]);
  const orderedIds = useMemo(() => sorted.map((c) => c.id), [sorted]);

  const pageSize = prefs.pageSize;
  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, pageCount - 1);
  const rows = useMemo(
    () => sorted.slice(currentPage * pageSize, (currentPage + 1) * pageSize),
    [sorted, currentPage, pageSize]
  );

  useEffect(() => {
    if (!loading && page > pageCount - 1) setPage(pageCount - 1);
  }, [loading, page, pageCount, setPage]);

  const visibleColumns = useMemo(
    () => prefs.columns.map((id) => config.columnsById[id]).filter(Boolean),
    [prefs.columns, config]
  );

  const filterDefs = useMemo(
    () => (config.getFilterDefs ? config.getFilterDefs(allCases, filters) : []),
    [allCases, filters, config]
  );

  // Ids with a check in flight, from the header's `checking` list (shared by
  // all case types, so it is filtered by category).
  const pendingFilter = config.pendingFilter ?? DEFAULT_PENDING_FILTER;
  const pendingCheckIds = useMemo(
    () =>
      new Set(
        (Array.isArray(checking) ? checking : [])
          .filter(pendingFilter)
          .map((c) => String(c.case_id))
      ),
    [checking, pendingFilter]
  );

  // Types without server-side polling refetch when their checks finish.
  const prevPendingRef = useRef(pendingCheckIds);
  useEffect(() => {
    const prev = prevPendingRef.current;
    prevPendingRef.current = pendingCheckIds;
    if (!config.refreshOnCheckDone) return;
    if ([...prev].some((id) => !pendingCheckIds.has(id))) refresh?.();
  }, [pendingCheckIds, config.refreshOnCheckDone, refresh]);

  const selection = useRowSelection(orderedIds);
  const selectedIds = useMemo(
    () => orderedIds.filter((id) => selection.selected.has(id)),
    [orderedIds, selection.selected]
  );

  const openCase = openId !== null ? allCases.find((c) => c.id === openId) : null;

  // ---- panel --------------------------------------------------------------
  const openInPanel = useCallback(
    (id) => {
      setFocusedId(id);
      // First open adds a history entry (Back closes the panel); moving
      // between cases while it is open replaces it.
      setOpenId(id, { history: openId === null ? "push" : "replace" });
    },
    [openId, setOpenId]
  );
  const closePanel = useCallback(() => setOpenId(null, { history: "replace" }), [setOpenId]);

  // Restore the list title when the panel closes.
  useEffect(() => {
    if (openId === null) document.title = `${config.title} | MySolon.gr`;
  }, [openId, config.title]);

  // Deep links (?case=…) focus the opened case.
  useEffect(() => {
    if (openId !== null) setFocusedId(openId);
  }, [openId]);

  const handleRowClick = useCallback(
    (id, e) => {
      if ((e?.ctrlKey || e?.metaKey) && fullPageHref) {
        window.open(fullPageHref(id), "_blank", "noopener");
        return;
      }
      openInPanel(id);
    },
    [openInPanel, fullPageHref]
  );

  const handleDelete = useCallback(
    async (id) => {
      const deleted = await mutations.deleteOne(id);
      if (deleted && openId === id) closePanel();
    },
    [mutations, openId, closePanel]
  );

  // ---- selection / bulk ---------------------------------------------------
  const handleSelectPage = useCallback(
    (checked) => selection.setMany(rows.map((r) => r.id), checked),
    [selection, rows]
  );

  const handleBulkRecheck = useCallback(async () => {
    if (
      caps.checkAll &&
      selectedIds.length > RECHECK_CAP &&
      selectedIds.length === allCases.length
    ) {
      if (
        window.confirm(
          "Επιλέξατε όλες τις υποθέσεις. Να ξεκινήσει ο μαζικός έλεγχος όλων (επιτρέπεται μία φορά την ημέρα);"
        )
      ) {
        await mutations.checkAll();
      }
      return;
    }
    await mutations.bulkRecheck(selectedIds);
  }, [caps.checkAll, selectedIds, allCases.length, mutations]);

  const handleBulkDelete = useCallback(async () => {
    const deleted = await mutations.bulkDelete(selectedIds);
    if (deleted.length) {
      selection.clear();
      if (openId !== null && deleted.includes(openId)) closePanel();
    }
  }, [mutations, selectedIds, selection, openId, closePanel]);

  // ---- keyboard -----------------------------------------------------------
  const moveFocus = useCallback(
    (delta) => {
      if (!orderedIds.length) return;
      const current = focusedId !== null ? orderedIds.indexOf(focusedId) : -1;
      let nextIndex;
      if (current === -1) {
        nextIndex =
          delta > 0
            ? currentPage * pageSize
            : Math.min(orderedIds.length, (currentPage + 1) * pageSize) - 1;
      } else {
        nextIndex = Math.max(0, Math.min(orderedIds.length - 1, current + delta));
      }
      const nextId = orderedIds[nextIndex];
      const nextPage = Math.floor(nextIndex / pageSize);
      if (nextPage !== currentPage) setPage(nextPage);
      setFocusedId(nextId);
      if (openId !== null) setOpenId(nextId, { history: "replace" });
    },
    [orderedIds, focusedId, currentPage, pageSize, setPage, openId, setOpenId]
  );

  const targetId = focusedId ?? openId;
  const openFullPage = () =>
    targetId !== null && fullPageHref ? navigate(fullPageHref(targetId)) : false;

  useHotkeys(
    {
      j: () => moveFocus(1),
      ArrowDown: () => moveFocus(1),
      k: () => moveFocus(-1),
      ArrowUp: () => moveFocus(-1),
      Enter: () => (focusedId !== null ? openInPanel(focusedId) : false),
      o: openFullPage,
      "Shift+Enter": openFullPage,
      "/": () => searchRef.current?.focus(),
      Escape: (e) => {
        if (e.target === searchRef.current) {
          searchRef.current.blur();
          return true;
        }
        if (openId !== null) return closePanel();
        if (selection.selected.size) return selection.clear();
        return false;
      },
      x: () => (focusedId !== null ? selection.toggle(focusedId) : false),
      s: () => (caps.fav && targetId !== null ? mutations.toggleFav(targetId) : false),
      r: () => (caps.recheck && targetId !== null ? mutations.recheckOne(targetId) : false),
      "[": () => setPage(Math.max(0, currentPage - 1)),
      "]": () => setPage(Math.min(pageCount - 1, currentPage + 1)),
      "?": () => setShowHelp(true),
    },
    { enabled: !showAddCase && !showHelp }
  );

  const hiddenShortcuts = [
    !caps.fav && "fav",
    !caps.recheck && "recheck",
    !fullPageHref && "page",
  ].filter(Boolean);

  // ---- split --------------------------------------------------------------
  const paneMode = openId === null ? "overview" : "detail";
  const { splitRef, splitStyle, startResize, resetWidth } = useSplitPane(
    config.scope,
    paneMode
  );

  // ---- add case modal -----------------------------------------------------
  // The classic add forms call their close callback with no args on
  // success/close, and some with the keydown event while typing; only
  // Escape (or no event) closes.
  const handleCloseRequest = useCallback((e) => {
    if (!e?.key || e.key === "Escape") setShowAddCase(false);
  }, []);

  const handleAdded = useCallback(
    (row) => {
      const { insert = false, refreshDelays = [] } = config.afterAdd ?? {};
      if (insert && row) addCase(row);
      if (row?.id) setFocusedId(row.id);
      refreshDelays.forEach((ms) =>
        ms ? window.setTimeout(() => refresh?.(), ms) : refresh?.()
      );
      // An inserted row without an id means the form gave up.
      if (!insert || row?.id) notify(config.addedText ?? "Η υπόθεση προστέθηκε.", "ok");
    },
    [config, addCase, refresh, notify]
  );

  const { AddForm, Overview } = config;
  const panelActions = {
    onToggleFav: caps.fav ? mutations.toggleFav : undefined,
    onRecheck: caps.recheck ? mutations.recheckOne : undefined,
    onSaveNote: config.noteInPanel && caps.note ? mutations.saveNote : undefined,
  };

  return (
    <div className={`ds-astika ds-caselist ds-caselist--${config.scope}`}>
      <CasesToolbar
        ref={searchRef}
        search={search}
        onSearch={setSearch}
        searchPlaceholder={config.searchPlaceholder}
        noun={config.noun}
        filters={filters}
        onFilter={setFilter}
        filterDefs={filterDefs}
        activeFilterCount={activeCount}
        onClearFilters={clearFilters}
        info={config.info}
        total={allCases.length}
        filteredTotal={sorted.length}
        addLabel={config.addLabel}
        onAddCase={AddForm ? () => setShowAddCase(true) : undefined}
        onCheckAll={caps.checkAll ? mutations.checkAll : undefined}
        bulkChecking={mutations.bulkChecking}
        columns={config.columns}
        visibleColumnIds={prefs.columns}
        onToggleColumn={toggleColumn}
        onResetColumns={resetColumns}
        onShowHelp={() => setShowHelp(true)}
        extra={config.toolbarExtra ? config.toolbarExtra(allCases) : null}
      />

      {selectedIds.length > 0 && (
        <BulkActionBar
          count={selectedIds.length}
          filteredTotal={sorted.length}
          allFav={selectedIds.every((id) => isFav(allCases.find((c) => c.id === id)))}
          progress={mutations.progress}
          onSelectAllFiltered={() => selection.setMany(orderedIds, true)}
          onFav={caps.fav ? () => mutations.bulkFav(selectedIds) : undefined}
          onRecheck={caps.recheck && config.bulkRecheck ? handleBulkRecheck : undefined}
          onDelete={handleBulkDelete}
          onClear={selection.clear}
        />
      )}

      {notice && (
        <div className={`ds-notice ds-notice--${notice.tone}`} role="status">
          {notice.text}
          <button type="button" className="ds-link-btn" onClick={() => setNotice(null)}>
            OK
          </button>
        </div>
      )}

      <div
        className={`ds-split ds-split--${paneMode}`}
        ref={splitRef}
        style={splitStyle}
      >
        <div className="ds-table-pane">
          <div className="ds-table-scroll">
            {loading ? (
              <div className="ds-empty">
                <span className="ds-spinner" aria-hidden="true" /> Φόρτωση…
              </div>
            ) : error && allCases.length === 0 ? (
              <div className="ds-empty">
                Δεν ήταν δυνατή η φόρτωση της λίστας.
                <button type="button" className="ds-btn" onClick={() => refresh?.()}>
                  Δοκιμάστε ξανά
                </button>
              </div>
            ) : allCases.length === 0 ? (
              <div className="ds-empty">
                {config.emptyText ?? "Δεν έχετε καταχωρήσει υποθέσεις."}
                {AddForm && (
                  <button
                    type="button"
                    className="ds-btn ds-btn--primary"
                    onClick={() => setShowAddCase(true)}
                  >
                    {config.addLabel ?? "Προσθήκη"}
                  </button>
                )}
              </div>
            ) : sorted.length === 0 ? (
              <div className="ds-empty">
                Δεν βρέθηκαν {config.noun.many} με αυτά τα κριτήρια.
                {(activeCount > 0 || search) && (
                  <button
                    type="button"
                    className="ds-link-btn"
                    onClick={() => {
                      clearFilters();
                      setSearch("");
                    }}
                  >
                    Καθαρισμός αναζήτησης και φίλτρων
                  </button>
                )}
              </div>
            ) : (
              <CasesTable
                rows={rows}
                columns={visibleColumns}
                sort={prefs.sort}
                onSort={cycleSort}
                selected={selection.selected}
                onToggleSelect={selection.toggle}
                onSelectPage={handleSelectPage}
                focusedId={focusedId}
                openId={openId}
                onRowClick={handleRowClick}
                onToggleFav={mutations.toggleFav}
                pendingCheckIds={pendingCheckIds}
                getRowLabel={config.getRowLabel}
                showFav={caps.fav}
                renderStatus={config.renderStatus}
                ariaLabel={config.title}
              />
            )}
          </div>
          {sorted.length > 0 && (
            <TablePagination
              page={currentPage}
              pageSize={pageSize}
              total={sorted.length}
              onPage={(p) => setPage(Math.max(0, Math.min(pageCount - 1, p)))}
              onPageSize={(size) => {
                setPageSize(size);
                setPage(0);
              }}
            />
          )}
        </div>

        <div
          className="ds-resizer"
          role="separator"
          aria-orientation="vertical"
          aria-label="Αλλαγή πλάτους πλαισίου"
          title="Σύρετε για αλλαγή πλάτους · διπλό κλικ για επαναφορά"
          onPointerDown={startResize}
          onDoubleClick={resetWidth}
        />

        <div className="ds-detail-pane">
          {openId !== null ? (
            <CaseDetailPanel
              caseId={openId}
              rowCase={openCase}
              pendingCheck={pendingCheckIds.has(String(openId))}
              onClose={closePanel}
              onDelete={handleDelete}
              {...panelActions}
              endpoints={detail.endpoints}
              getTitle={detail.getTitle}
              getSubtitle={detail.getSubtitle}
              getRefreshKey={detail.getRefreshKey}
              fullPageHref={fullPageHref}
              documentTitle={detail.documentTitle}
              renderBody={
                detail.renderBody
                  ? (args) =>
                      detail.renderBody({
                        ...args,
                        updateRow: (patch) => updateCase(openId, patch),
                      })
                  : undefined
              }
              ariaLabel={detail.ariaLabel}
            />
          ) : (
            <div className="ds-overview">
              {filtered.length > 0 ? (
                <Overview rows={filtered} onOpen={openInPanel} />
              ) : (
                !loading && (
                  <div className="ds-empty">Επιλέξτε μια εγγραφή για να δείτε τα στοιχεία της.</div>
                )
              )}
            </div>
          )}
        </div>
      </div>

      {showAddCase && AddForm && (
        <div
          className="ds-modal-backdrop"
          data-ds-modal-open="true"
          onMouseDown={() => setShowAddCase(false)}
        >
          <div
            className={`ds-modal ${config.modalClass ?? "ds-modal--form"}`}
            role="dialog"
            aria-modal="true"
            aria-label={config.addLabel ?? "Προσθήκη"}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <AddForm
              onClose={handleCloseRequest}
              onAdded={handleAdded}
              allCases={allCases}
              credits={credits}
              setCredits={setCredits}
            />
          </div>
        </div>
      )}

      {showHelp && (
        <ShortcutsHelp onClose={() => setShowHelp(false)} hide={hiddenShortcuts} />
      )}
    </div>
  );
};

export default CaseListDesktop;
